/**
 * The booking widget — EasyPlanner running inside an iframe on a business's own
 * site. `public/embed.js` is the loader the business pastes; `/embed/:slug` is
 * the page it frames; this file is what the two sides and the dashboard's
 * snippet generator have to agree on.
 *
 * The page is mounted by `index.jsx` *instead of* `<App>`, not as a route in it.
 * App reads localStorage on its first render (the session check), and in a
 * third-party iframe a browser that blocks third-party storage throws on that
 * read — the widget would be a blank rectangle for exactly those visitors.
 * Nothing on the widget's path needs a session, so it never goes near one.
 */

import { darken, normalizeHex, onBrandText } from "./color";

export const EMBED_PATH_PREFIX = "/embed/";

export const EMBED_MODES = ["popup", "inline"];

/** Posted to the host page, so it can tell our messages from anyone else's. */
export const EMBED_MESSAGE_SOURCE = "easyplanner";

/** The languages the widget can be asked for; anything else is ignored. */
export const EMBED_LANGUAGES = ["ro", "en"];

export const isEmbedPath = (pathname) =>
  typeof pathname === "string" && pathname.startsWith(EMBED_PATH_PREFIX);

/**
 * Reads `/embed/:slug?mode=&lang=` into what the widget needs, or null when the
 * path is not an embed path or carries no slug. Unknown modes fall back to
 * inline and unknown languages to none, rather than failing — the loader may be
 * older or newer than this page.
 */
export const parseEmbedLocation = ({ pathname, search }) => {
  if (!isEmbedPath(pathname)) return null;

  const rawSlug = pathname.slice(EMBED_PATH_PREFIX.length).split("/")[0];
  let slug;
  try {
    slug = decodeURIComponent(rawSlug);
  } catch {
    return null;
  }
  if (!slug) return null;

  const params = new URLSearchParams(search || "");
  const mode = EMBED_MODES.includes(params.get("mode")) ? params.get("mode") : "inline";
  const lang = EMBED_LANGUAGES.includes(params.get("lang")) ? params.get("lang") : null;

  return { organizationSlug: slug, mode, lang };
};

// Re-exported: the widget's callers and tests have always imported it from here.
export { normalizeHex };

/** The attribute that makes any element on the business's page open the popup. */
export const EMBED_OPEN_ATTRIBUTE = "data-easyplanner-open";

/**
 * The business's colour as the custom properties BookingModal.css reads, or an
 * empty object — leaving the stylesheet's own green — when the value is not a
 * hex colour. The value comes from stored settings, and interpolating an
 * unchecked string into a custom property lets it close the declaration and
 * add its own.
 *
 * The text colour on the accent is picked for contrast rather than assumed to
 * be white: a business whose colour is pale yellow would otherwise get white
 * labels on its selected slots and nobody could read which one is selected.
 * The same rule as the business's site and the floating button (`onBrandText`).
 */
export const accentStyleFor = (color) => {
  const hex = normalizeHex(color);
  if (!hex) return {};

  return {
    "--booking-accent": hex,
    "--booking-accent-hover": darken(hex, 0.12),
    "--booking-on-accent": onBrandText(hex),
  };
};

/** Tells the page that framed us something. Never carries personal data: the
    target origin is "*" because we cannot know the business's domain, so
    anything posted here is readable by whatever page embedded the widget. */
export const postToHost = (type, payload = {}) => {
  if (typeof window === "undefined" || window.parent === window) return;
  window.parent.postMessage({ source: EMBED_MESSAGE_SOURCE, type, ...payload }, "*");
};

const escapeAttribute = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeText = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * What a business pastes into its own site.
 *
 * One `<script>` for both modes, so there is only one thing to explain: inline,
 * the calendar appears exactly where the tag is pasted; as a popup, the tag
 * adds a floating button in the business's colour.
 *
 * With `ownButton` the popup gets no floating button (`data-button="none"`) and
 * the snippet carries a button of the business's own, marked with the attribute
 * any element on its page can carry to open the window — so what is pasted
 * works as it stands. The label and colour are left out wherever there is no
 * floating button for them to apply to.
 */
export const buildEmbedSnippet = ({ origin, slug, mode, label, color, ownButton = false }) => {
  const popup = mode === "popup";
  const text = (label || "").trim();
  const attributes = [
    `src="${escapeAttribute(`${origin}/embed.js`)}"`,
    `data-slug="${escapeAttribute(slug)}"`,
    `data-mode="${popup ? "popup" : "inline"}"`,
  ];

  if (popup && ownButton) {
    attributes.push('data-button="none"');
  } else if (popup) {
    const hex = normalizeHex(color);
    if (text) attributes.push(`data-label="${escapeAttribute(text)}"`);
    if (hex) attributes.push(`data-color="${hex}"`);
  }

  const script = `<script ${attributes.join(" ")} async></script>`;
  if (!popup || !ownButton) return script;
  return `${script}\n<button type="button" ${EMBED_OPEN_ATTRIBUTE}>${escapeText(text)}</button>`;
};
